/* ═══════════════════════════════════════════════════════════
   ROUGHCUT COMMUNITY PAGE — rc-community.js
═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Guarded because this is the first executable line in the file and it sits
     outside any try/catch. If it throws, nothing below it is ever defined —
     the tab filter, the build-card modal and the whole reveal pass on this
     page die together, with no error anyone would see. matchMedia exists in
     every browser that matters, so this will essentially never fire; it costs
     one conditional to make the failure mode "no reduced-motion support"
     instead of "the community page does nothing". */
  var mm = window.matchMedia;
  const reduceMotion = mm ? mm.call(window, '(prefers-reduced-motion: reduce)').matches : false;

  /* ── INTERSECTION OBSERVER (scroll reveals) ─────────────── */
  function initScrollAnimations() {
    if (reduceMotion) {
      document.querySelectorAll('.rc-anim').forEach(el => el.classList.add('rc-anim--in'));
      document.querySelectorAll('.rc-anim-group').forEach(el => el.classList.add('rc-anim--in'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = parseInt(el.dataset.delay || 0);
        setTimeout(() => el.classList.add('rc-anim--in'), delay);
        observer.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.rc-community .rc-anim, .rc-community .rc-anim-group').forEach(el => observer.observe(el));
  }

  /* Stagger grid items via JS */
  function initGridStagger() {
    const items = document.querySelectorAll('.rc-build-item');
    if (!items.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const idx = parseInt(el.dataset.idx || 0);
        if (reduceMotion) {
          el.style.opacity = '1';
          el.style.transform = 'none';
        } else {
          setTimeout(() => {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }, idx * 35);
        }
        observer.unobserve(el);
      });
    }, { threshold: 0.05 });

    items.forEach((el, i) => {
      el.dataset.idx = i;
      if (!reduceMotion) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(16px)';
        el.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
      }
      observer.observe(el);
    });
  }

  /* ── COUNTER ANIMATION ──────────────────────────────────── */
  function countUp(el) {
    if (reduceMotion) {
      el.textContent = el.dataset.target + (el.dataset.suffix || '');
      return;
    }
    const target = parseInt(el.dataset.target);
    const suffix = el.dataset.suffix || '';
    const duration = 1600;
    const start = performance.now();
    const update = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.floor(eased * target).toLocaleString() + suffix;
      if (progress < 1) requestAnimationFrame(update);
    };
    requestAnimationFrame(update);
  }

  function initCounters() {
    document.querySelectorAll('.rc-community .rc-counter').forEach(el => countUp(el));
  }

  /* ── FILM CAROUSEL ARROWS ───────────────────────────────── */
  function initCarousel() {
    const wrap = document.querySelector('.rc-films__wrap');
    if (!wrap) return;
    const track = wrap.querySelector('.rc-films__track');
    const prev  = wrap.querySelector('.rc-films__arrow--prev');
    const next  = wrap.querySelector('.rc-films__arrow--next');
    if (!track || !prev || !next) return;

    const card = track.querySelector('.rc-film-card');
    function scrollAmount() {
      if (!card) return 300;
      return card.offsetWidth + 20;
    }

    prev.addEventListener('click', () => track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left:  scrollAmount(), behavior: 'smooth' }));
  }

  /* ── BUILDS RAIL: FILTER, COUNT, ARROWS ─────────────────────
     The filter used to only fade non-matching builds to opacity:0 — they
     kept their grid cells, so filtering to "Level 1" left a screen of
     holes. In a sideways rail that would be worse: you would scroll
     through empty space. They are now removed from layout, the count
     follows the filter, and the arrows disable at each end. */
  function initFilter() {
    const tabs  = document.querySelectorAll('.rc-filter-tab');
    /* the last tile is the "Add yours" ask, not a build — it must not be
       filtered out and must not be counted */
    const items = document.querySelectorAll('.rc-build-item:not(.rc-build-item--cta)');
    const rail  = document.querySelector('.rc-builds-grid');
    const wrap  = document.querySelector('.rc-builds-wrap');
    const count = document.getElementById('rcBuildCount');
    if (!tabs.length || !items.length) return;

    const prev = document.querySelector('.rc-builds__arrow--prev');
    const next = document.querySelector('.rc-builds__arrow--next');

    function syncArrows() {
      if (!rail || !prev || !next) return;
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
    }

    function apply(level) {
      let shown = 0;
      items.forEach(item => {
        const show = level === 'all' || item.dataset.level === level;
        item.classList.toggle('is-hidden', !show);
        if (show) shown++;
      });
      if (count) {
        count.innerHTML = '<b>' + shown + '</b> ' +
          (level === 'all' ? 'builds shared' : 'at level ' + level);
      }
      if (wrap) wrap.classList.toggle('is-empty', shown === 0);
      if (rail) rail.scrollTo({ left: 0, behavior: 'smooth' });
      setTimeout(syncArrows, 350);
    }

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        apply(tab.dataset.level);
      });
    });

    if (rail && prev && next) {
      /* a whole "page" of the rail, so it never lands mid-thumbnail */
      const step = () => Math.round(rail.clientWidth * 0.82);
      prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => rail.scrollBy({ left:  step(), behavior: 'smooth' }));
      rail.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows);
      syncArrows();
    }
  }

  /* ── ADMIN CARD FLIP ────────────────────────────────────── */
  function initAdminFlip() {
    document.querySelectorAll('.rc-admin-card').forEach(card => {
      card.addEventListener('click', () => card.classList.toggle('is-flipped'));
    });
  }

  /* ── YOUTUBE MODAL ──────────────────────────────────────── */
  function initYouTubeModal() {
    const modal  = document.getElementById('rc-yt-modal');
    const frame  = document.getElementById('rc-yt-frame');
    const closer = document.getElementById('rc-yt-close');
    if (!modal || !frame || !closer) return;

    function open(videoId) {
      frame.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0';
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      frame.src = '';
      modal.classList.remove('open');
      document.body.style.overflow = '';
    }

    document.querySelectorAll('.rc-film-card[data-yt]').forEach(card => {
      card.addEventListener('click', () => {
        const id = card.dataset.yt;
        if (id) open(id);
      });
    });

    closer.addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  /* ── INIT ───────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initScrollAnimations();
    initGridStagger();
    initCounters();
    initCarousel();
    initFilter();
    initAdminFlip();
    initYouTubeModal();
  });
})();
